'use client';

import React, { useState, useRef } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalInput } from './GlobalInput';
import { useToast } from '../context/ToastProvider';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import { useBatchProgress } from '../context/BatchProgressContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import styles from './EntityGenerationSidebar.module.css';
import { fileIconFromFileName } from '../utils/fileTypeGraphics';

const ASYNC_INGEST_THRESHOLD_MB = 12;
const ASYNC_INGEST_THRESHOLD_BYTES = ASYNC_INGEST_THRESHOLD_MB * 1024 * 1024;

function shouldUseAsyncIngestion(file: { filename: string; contentType: string; size: number }): boolean {
  const lowerName = file.filename.toLowerCase();
  const isPdf = file.contentType === 'application/pdf' || lowerName.endsWith('.pdf');
  // PDFs can be slow due to OCR/chunking; force async to avoid 540s request timeout.
  if (isPdf) return true;
  return file.size >= ASYNC_INGEST_THRESHOLD_BYTES;
}

interface AddKnowledgeDocumentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeId: string;
  companyId: string; // Company ref for documents/chunks (user's company on home, viewed company on company page)
  onSuccess?: () => void;
}

export default function AddKnowledgeDocumentSidebar({
  isOpen,
  onClose,
  knowledgeId,
  companyId,
  onSuccess,
}: AddKnowledgeDocumentSidebarProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const { user } = useUser();
  const { startBatch, endBatch } = useBatchProgress();
  const isDarkMode = theme === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentName, setDocumentName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setDocumentName('');
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (selectedFiles.length === 1 && !documentName) {
      setDocumentName(selectedFiles[0].name.replace(/\.[^/.]+$/, ''));
    }
  }, [selectedFiles, documentName]);

  const allowedTypes = ['application/pdf', 'text/csv', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const maxSize = 24 * 1024 * 1024;

  const handleFileSelect = () => fileInputRef.current?.click();

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const fileList = Array.from(files);
    const invalid = fileList.find((f) => !allowedTypes.includes(f.type));
    if (invalid) {
      showToast(t('project_tab_invalid_file_type') || 'Invalid file type', 'error');
      return;
    }
    const tooLarge = fileList.find((f) => f.size > maxSize);
    if (tooLarge) {
      showToast(t('project_tab_file_too_large') || 'File size must be less than 24MB', 'error');
      return;
    }
    setSelectedFiles(fileList);
  };

  const handleRemoveFile = (index: number) => {
    const next = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(next);
    if (next.length === 0) setDocumentName('');
  };

  const handleConfirm = async () => {
    if (!selectedFiles.length) {
      showToast(t('add_document_no_file_selected') || 'Please select a file', 'error');
      return;
    }
    if (!user) {
      showToast(t('add_document_not_authenticated') || 'You must be logged in to upload documents', 'error');
      return;
    }

    setIsLoading(true);
    onClose();

    showToast(t('add_document_uploading') || 'Uploading document...', 'info');

    const ingestUrl =
      process.env.NEXT_PUBLIC_INGEST_DOCUMENT_URL ||
      process.env.REACT_APP_INGEST_DOCUMENT_URL ||
      'https://europe-west3-thescon-app.cloudfunctions.net/ingestDocument';
    const ingestAsyncUrl =
      process.env.NEXT_PUBLIC_INGEST_DOCUMENT_ASYNC_URL ||
      process.env.REACT_APP_INGEST_DOCUMENT_ASYNC_URL ||
      'https://europe-west3-thescon-app.cloudfunctions.net/ingestDocumentAsync';

    try {
      const storage = getStorage();
      const filesWithUrls: Array<{ fileUrl: string; filename: string; storagePath: string; contentType: string; size: number }> = [];

      for (const file of selectedFiles) {
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').replace(/\s+/g, '_').substring(0, 100);
        const fileName = `knowledge/${knowledgeId}/${Date.now()}_${sanitizedFileName}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        filesWithUrls.push({ fileUrl: downloadURL, filename: file.name, storagePath: fileName, contentType: file.type, size: file.size });
      }

      startBatch(filesWithUrls.length, 'documents');
      showToast(t('add_document_processing_description') || 'Processing document...', 'info');

      let succeeded = 0;
      let queued = 0;
      let failed = 0;
      for (const meta of filesWithUrls) {
        const documentId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        const useAsync = shouldUseAsyncIngestion(meta);
        const endpoint = useAsync ? ingestAsyncUrl : ingestUrl;
        const nextStatus = useAsync ? 'queued' : 'processing';

        await setDoc(
          doc(db, 'knowledge', knowledgeId, 'documents', documentId),
          {
            documentId,
            filename: meta.filename,
            fileUrl: meta.fileUrl,
            storagePath: meta.storagePath,
            contentType: meta.contentType,
            type: 'knowledge_document',
            status: nextStatus,
            userId: user.uid,
            knowledgeId,
            companyId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileUrl: meta.fileUrl,
            userId: user.uid,
            meta: {
              documentId,
              knowledgeId,
              companyId,
              filename: meta.filename,
              sourceFileUrl: meta.fileUrl,
            },
            persist: {
              enabled: true,
              replaceExistingChunks: false,
              omitEmbeddingsInResponse: true,
            },
          }),
        });

        if (!response.ok) {
          failed += 1;
          await setDoc(
            doc(db, 'knowledge', knowledgeId, 'documents', documentId),
            {
              status: 'failed',
              embeddingError: `HTTP ${response.status}`,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          continue;
        }

        if (useAsync) {
          const queuedResult = await response.json().catch(() => ({})) as { jobId?: string };
          await setDoc(
            doc(db, 'knowledge', knowledgeId, 'documents', documentId),
            {
              ingestJobId: queuedResult.jobId || null,
              ingestMode: 'async',
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          queued += 1;
          succeeded += 1;
          continue;
        }

        const result = await response.json().catch(() => ({})) as { documentId?: string };
        if (!result.documentId) {
          failed += 1;
          await setDoc(
            doc(db, 'knowledge', knowledgeId, 'documents', documentId),
            {
              status: 'failed',
              embeddingError: 'Missing documentId in response',
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          continue;
        }

        succeeded += 1;
      }

      if (failed > 0) {
        showToast(t('add_document_batch_partial') || `Processed ${succeeded} document(s), ${failed} failed.`, 'warning');
      } else {
        if (queued > 0) {
          showToast(
            queued === succeeded
              ? (t('add_document_batch_queued_all') || `${queued} large document(s) queued. Processing continues in the background.`)
              : (t('add_document_batch_queued_partial') || `${queued} large document(s) queued in background, ${succeeded - queued} started immediately.`),
            'info'
          );
        } else {
          showToast(
            succeeded === 1
              ? (t('add_document_success') || 'Document added successfully')
              : (t('add_document_batch_success') || `${succeeded} documents added successfully`),
            'success'
          );
        }
      }

      setSelectedFiles([]);
      setDocumentName('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      onSuccess?.();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('Add knowledge document error:', error);
      // Failed to fetch + ERR_NETWORK_CHANGED: browser dropped the long request (Wi‑Fi/VPN/sleep).
      // The server may still finish processing; backend also writes document metadata to Firestore.
      const looksLikeNetwork =
        msg === 'Failed to fetch' ||
        msg === 'Load failed' ||
        (error instanceof TypeError && /fetch|network|aborted/i.test(msg));
      showToast(
        looksLikeNetwork
          ? t('add_document_error_network') ||
              'Connection interrupted (often after a long wait). Processing may still finish on the server—refresh the library in a minute. If nothing appears, try again on a stable network.'
          : t('add_document_error') || `Failed to add document: ${msg}`,
        'error'
      );
    } finally {
      endBatch();
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
        <div className={styles.header}>
          <img src={isDarkMode ? '/assets/identity/logo-dark.svg' : '/assets/identity/logo.svg'} alt="Thescon" width={16} height={16} />
          <span>|</span>
          <h2>{t('add_document_title') || 'Add Document'}</h2>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('add_document_select_file') || 'Select File'}</h3>
          <p className={styles.sectionDescription}>
            {t('add_document_select_file_description') || 'Choose a document to upload to this collection'}
          </p>

          {selectedFiles.length === 0 ? (
            <div style={{ display: 'inline-block' }}>
              <GlobalButton
                onClick={handleFileSelect}
                variant="secondary"
                disabled={isLoading}
                autoWidth
                icon={<img src={isDarkMode ? '/assets/icons/icloud.and.arrow.up.svg' : '/assets/icons/icloud.and.arrow.up.black.svg'} alt="Upload" width={16} height={16} />}
              >
                {t('add_document_choose_files') || 'Choose File(s)'}
              </GlobalButton>
            </div>
          ) : (
            <div className={styles.fileList}>
              {selectedFiles.map((file, index) => {
                const fileIcon = fileIconFromFileName(file.name, isDarkMode);
                return (
                <div key={`${file.name}-${index}`} className={styles.filePreview}>
                  <div className={styles.fileInfo}>
                    <div className={styles.fileIcon}>
                      <img src={fileIcon.src} alt={fileIcon.alt} width={24} height={24} />
                    </div>
                    <div className={styles.fileDetails}>
                      <div className={styles.fileName}>{file.name}</div>
                      <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  <GlobalButton onClick={() => handleRemoveFile(index)} variant="secondary" disabled={isLoading} destructive icon={<img src="/assets/icons/trash.red.svg" alt="Remove" width={12} height={12} />}>
                    {t('add_document_remove') || 'Remove'}
                  </GlobalButton>
                </div>
                );
              })}
            </div>
          )}

          <input ref={fileInputRef} type="file" accept=".pdf,.csv,.doc,.docx,image/jpeg,image/png" multiple onChange={handleFileInputChange} style={{ display: 'none' }} disabled={isLoading} />
        </div>

        {selectedFiles.length === 1 && (
          <div className={styles.section}>
            <GlobalInput
              label={t('add_document_name') || 'Document name'}
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={isLoading}
              placeholder={t('add_document_name_placeholder') || 'Enter document name'}
            />
          </div>
        )}

        <div className={styles.actionSection}>
          <GlobalButton onClick={handleConfirm} disabled={isLoading || selectedFiles.length === 0} isLoading={isLoading}>
            {isLoading ? (t('add_document_uploading') || 'Uploading...') : (t('add_document_confirm') || 'Add Document')}
          </GlobalButton>
        </div>
      </div>
    </GlobalSidebar>
  );
}
